/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable react/jsx-handler-names */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import ContactSupportIcon from '@/Assets/Images/contact-support.svg?url';
import LanguageIcon from '@/Assets/Images/language-icon.svg?url';
import BackgroundImage from '@/Assets/Images/stage-background.png';
import Logo from '@/Assets/Images/swiftpal-logo.svg?url';
import DialogBox4, { DialogBox4Reference } from '@/components/DialogBox4';
import InputField from '@/components/InputField';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import useLanguage from '@/hooks/useLanguage';
import { useSnackbar } from '@/hooks/useSnackbar';
import useValidation from '@/hooks/util/useValidation';
import LocationHeader from '@/pages/AtHomeScreen/components/LocationHeader';
import { useAtHomeProgramAndAccessSchema } from '@/pages/AtHomeScreen/schema/useAtHomeProgramAndAccessSchema';
import { PRIVACY_URL, TERMS_URL } from '@/pages/CenterValidation/components/CenterValidationDialog';
import { AtHomeLoginResponse } from '@/services/api';

export type HomeFormData = {
  accessCode: string;
  programCode: string;
  mobile: string;
};

type Country = {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
};

const COUNTRIES: Country[] = [
  { code: 'IN', dialCode: '+91', flag: '🇮🇳', name: 'India' },
  // { code: 'US', dialCode: '+1', flag: '🇺🇸', name: 'United States' },
  // { code: 'GB', dialCode: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  // { code: 'AU', dialCode: '+61', flag: '🇦🇺', name: 'Australia' },
  // { code: 'CA', dialCode: '+1', flag: '🇨🇦', name: 'Canada' },
  // { code: 'SG', dialCode: '+65', flag: '🇸🇬', name: 'Singapore' },
  // { code: 'AE', dialCode: '+971', flag: '🇦🇪', name: 'UAE' },
  // { code: 'SA', dialCode: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  // { code: 'MY', dialCode: '+60', flag: '🇲🇾', name: 'Malaysia' },
  // { code: 'NP', dialCode: '+977', flag: '🇳🇵', name: 'Nepal' },
  // { code: 'LK', dialCode: '+94', flag: '🇱🇰', name: 'Sri Lanka' },
];

interface HomeSetupScreenProperties {
  onSubmitAtHomeAccessCode: (
    parameters: HomeFormData,
    onSuccess?: (data: AtHomeLoginResponse) => void,
  ) => void;
  onSubmitAtHomeMobile: (
    parameters: HomeFormData,
    dialCode: string,
    onSuccess?: (data: AtHomeLoginResponse) => void,
  ) => void;
}

export default function HomeSetupScreen({
  onSubmitAtHomeAccessCode,
  onSubmitAtHomeMobile,
}: HomeSetupScreenProperties): ReactElement {
  const { t } = useTranslation(['centerValidation', 'translation', 'atHome', 'studentLogin']);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dialogReference = useRef<DialogBox4Reference>(null);
  const onDismiss = useCallback(() => {
    if (dialogReference.current) dialogReference.current?.onDismiss();
  }, []);
  const {
    openLanguageDialog,
    isLanguageDialogOpen,
    dialogProperties: languageDialogProperties,
    isApplyingLanguage,
  } = useLanguage(onDismiss);
  const [formData, setFormData] = useState<HomeFormData>({
    accessCode: '',
    programCode: '',
    mobile: '',
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'accessCode' | 'phoneNumber'>('accessCode');
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);

  const atHomeAccessSchema = useAtHomeProgramAndAccessSchema();
  const { errors: accessCodeFieldErrors, validate, clearError } = useValidation(atHomeAccessSchema);
  const fieldErrors = selectedMode === 'accessCode' ? accessCodeFieldErrors : {};

  const isSubmitDisabled =
    (selectedMode === 'accessCode'
      ? !formData.programCode || !formData.accessCode
      : !formData.programCode || !formData.mobile) || !agreeToTerms;
  const isCheckboxDisabled =
    selectedMode === 'accessCode'
      ? !formData.programCode || !formData.accessCode
      : !formData.programCode || !formData.mobile;

  useEffect(() => {
    if (isCheckboxDisabled) {
      setAgreeToTerms(false);
    }
  }, [isCheckboxDisabled]);

  const validatePhoneNumber = (mobile: string) => {
    if (mobile.length < 10 || mobile.length > 10) {
      showSnackbar('Mobile number must be exactly 10 digits.');
      return false;
    }
    return true;
  };

  const onHandleVerify = () => {
    if (selectedMode === 'accessCode') {
      if (
        !validate({
          programCode: formData.programCode,
          accessCode: formData.accessCode,
        })
      ) {
        return;
      }
      onSubmitAtHomeAccessCode(formData);
    } else {
      if (!validatePhoneNumber(formData.mobile)) {
        return;
      }
      onSubmitAtHomeMobile(formData, selectedCountry.dialCode);
    }
  };

  const onChangeListeners = (field: keyof HomeFormData) => (value: string) => {
    if (field === 'mobile' && !/^\d*$/.test(value)) return;
    clearError(field);
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const onAgreeToTermsChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToTerms(event_.target.checked);
  };

  const onLanguageClick = () => {
    openLanguageDialog();
  };

  const menuOption = [
    {
      onClick: () => {
        onLanguageClick();
      },
      label: t('atHome:language'),
      icon: <img src={LanguageIcon} alt="Language" style={{ width: '24px', height: '24px' }} />,
    },
    {
      onClick: () => {
        window.open(Constants.CUSTOMER_SUPPORT_URL, '_blank');
      },
      label: t('atHome:contactSupport'),
      icon: (
        <img
          src={ContactSupportIcon}
          alt="support-icon"
          style={{ width: '24px', height: '24px' }}
        />
      ),
    },
  ];
  const handleBackClick = () => {
    navigate(ROUTES.ROOT);
  };
  const onHandlePhoneNumberClick = () => {
    // setSelectedMode('phoneNumber');
    showSnackbar(t('atHome:notEnabled'));
  };

  const renderTermsAndConditions = () => (
    <div className="mt-6 flex max-w-[584px] items-start space-x-3">
      <input
        id="terms"
        disabled={isCheckboxDisabled}
        type="checkbox"
        checked={agreeToTerms}
        onChange={onAgreeToTermsChange}
        className="mt-1 h-8 w-8 rounded border-2 border-neutral-white bg-none text-primary-blue-3 focus:ring-primary-blue-3"
      />
      <label htmlFor="terms" className="text-[1.125rem] font-semibold text-neutral-white">
        {t('centerValidation:byContinuingAgreeToSwiftPal')}{' '}
        <a
          target="_blank"
          href={TERMS_URL}
          className="text-secondary-blue-1 underline"
          rel="noreferrer"
        >
          {t('centerValidation:termsOfService')}
        </a>{' '}
        {t('centerValidation:and')}{' '}
        <a
          target="_blank"
          href={PRIVACY_URL}
          className="text-secondary-blue-1 underline"
          rel="noreferrer"
        >
          {t('centerValidation:userPolicy')}
        </a>
      </label>
    </div>
  );

  const renderInputField = (
    field: keyof HomeFormData,
    label: string,
    type: string,
    placeholder: string = '',
    error?: string,
  ) => (
    <div className="space-y-[10px]">
      <label
        htmlFor={field}
        className="font-poppims leading-1 block text-l2 font-semibold text-neutral-white"
      >
        {label}
      </label>
      <InputField
        id={field}
        type={type}
        value={formData[field]}
        onChange={onChangeListeners(field)}
        placeholder={placeholder}
        error={error}
        inputClassName="w-[584px] !bg-neutral-white rounded-xl border-2 border-neutral-grey-3 px-4 py-4 text-lg placeholder-neutral-grey-3 shadow-lg"
        required
      />
    </div>
  );

  return (
    <>
      <div
        className="absolute inset-0 flex h-screen w-screen flex-col bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url(${BackgroundImage})`,
        }}
      >
        <LocationHeader
          title={`${t('atHome:letSetUp')}`}
          menuOptions={menuOption}
          onBack={() => {
            handleBackClick();
          }}
        />
        <div className="flex w-full flex-1 items-start justify-center px-8 pt-[117px]">
          <div className="flex flex-col items-center">
            <div className="mb-4 flex w-[500px] rounded-full bg-secondary-blue-7 p-1 shadow-lg">
              <button
                type="button"
                onClick={() => setSelectedMode('accessCode')}
                className={`flex-1 rounded-full px-6 py-3 text-xl1 font-semibold ${
                  selectedMode === 'accessCode'
                    ? 'bg-neutral-white text-secondary-blue-8'
                    : 'text-neutral-white'
                }`}
              >
                {t('atHome:accessCode')}
              </button>
              <button
                type="button"
                onClick={onHandlePhoneNumberClick}
                className={`flex-1 rounded-full px-6 py-3 text-xl1 font-semibold ${
                  selectedMode === 'phoneNumber'
                    ? 'bg-neutral-white text-secondary-blue-8'
                    : 'text-neutral-white'
                }`}
              >
                {t('atHome:phoneNumber')}
              </button>
            </div>
            <div className="mt-4">
              {renderInputField(
                'programCode',
                t('atHome:enterProgramCode'),
                'text',
                'Eg: BS94D',
                fieldErrors.programCode?.[0],
              )}
            </div>
            <div className="mt-6">
              {selectedMode === 'accessCode' ? (
                renderInputField(
                  'accessCode',
                  t('atHome:enterPalAtHomeLearningCode'),
                  'text',
                  'Eg: BS94D',
                  fieldErrors.accessCode?.[0],
                )
              ) : (
                <div className="space-y-[10px]">
                  <label
                    htmlFor="phoneNumber"
                    className="font-poppims leading-1 block text-l2 font-semibold text-neutral-white"
                  >
                    {t('atHome:enterMobileNumber')}
                  </label>
                  <div className="flex gap-4">
                    <div className="relative w-[150px]">
                      <select
                        value={selectedCountry.code}
                        onChange={(event_) => {
                          const country = COUNTRIES.find((c) => c.code === event_.target.value);
                          if (country) setSelectedCountry(country);
                        }}
                        className="flex w-full cursor-pointer appearance-none items-center rounded-xl border-2 border-neutral-grey-3 !bg-neutral-white px-4 py-4 pr-8 text-lg font-medium shadow-lg"
                      >
                        {COUNTRIES.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.dialCode}
                          </option>
                        ))}
                      </select>
                    </div>
                    <InputField
                      id="phoneNumber"
                      type="tel"
                      value={formData.mobile}
                      onChange={onChangeListeners('mobile')}
                      placeholder="XXXXX XXXXX"
                      error={fieldErrors.mobile?.[0]}
                      inputClassName="flex-1 w-[420px] !bg-neutral-white rounded-xl border-2 border-neutral-grey-2 px-4 py-4 text-lg placeholder-neutral-grey-3 shadow-lg"
                      required
                    />
                  </div>
                </div>
              )}
            </div>
            {renderTermsAndConditions()}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={onHandleVerify}
                disabled={isSubmitDisabled}
                className={`font-poppins flex w-[584px] items-center justify-center rounded-full px-6 py-[25px] ${
                  isSubmitDisabled
                    ? 'cursor-not-allowed bg-primary-yellow-1 text-primary-blue-1'
                    : 'bg-primary-yellow-3 text-primary-blue-3'
                } text-[1.625rem] font-semibold`}
                style={{ boxShadow: '0px 4.37px 4.37px 0px #00000040' }}
              >
                {t('studentLogin:verify')}
              </button>
            </div>
            <div className="absolute bottom-8 right-8">
              <img src={Logo} alt="logo" className="h-full w-full" />
            </div>
          </div>
        </div>
      </div>
      {isLanguageDialogOpen && (
        <DialogBox4
          open={isLanguageDialogOpen}
          variant="blue"
          showCloseIcon={false}
          ref={dialogReference}
          onClose={languageDialogProperties.onClose}
        >
          {languageDialogProperties.component}
        </DialogBox4>
      )}
      {isApplyingLanguage && <Loader />}
    </>
  );
}
